import type { ChangeEvent } from "react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { getCloudSubscriptionExistsById } from "client/api/CloudSubscriptionApi";
import { getServerLicenseExistsById } from "client/api/ServerLicenseApi";
import { getUserExistsById } from "client/api/UsersApi";
import { cloudSubscriptionRoutes } from "areas/cloud-subscriptions/cloud-subscription-routes";
import { licenseRoutes } from "areas/licenses/license-routes";
import { UserRoutesMap } from "areas/users/UserRoutesMap";

const SupportedEntities: { apiCall: (id: string) => Promise<boolean>; redirectUrl: (id: string) => string }[] = [
  {
    apiCall: getCloudSubscriptionExistsById,
    redirectUrl: (id) => cloudSubscriptionRoutes.detail(id).root,
  },
  {
    apiCall: getServerLicenseExistsById,
    redirectUrl: (id) => licenseRoutes.detail(id).root,
  },
  {
    apiCall: getUserExistsById,
    redirectUrl: (id) => UserRoutesMap.detail(id).root,
  },
];

export function IdSearchDialog() {
  const [open, setOpen] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [nothingFound, setNothingFound] = useState<boolean>(false);
  const [searchId, setSearchId] = useState<string>("");

  const navigate = useNavigate();

  useEffect(() => {
    window.addEventListener("keydown", handleKeypress);

    return () => {
      window.removeEventListener("keydown", handleKeypress);
    };
  });

  useEffect(() => {
    if (!open) {
      setSearchId("");
      setIsSearching(false);
      setNothingFound(false);
    }
  }, [open]);

  const handleKeypress = async (e: KeyboardEvent) => {
    if (e.altKey && e.code === "KeyI") {
      setOpen(true);
      e.cancelBubble = true;
    } else if (e.code === "Enter") {
      if (searchId && open) {
        await performSearchAsync();
      }
    }
  };

  const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setSearchId(e.target.value);
  };

  const handleOnClose = () => {
    if (isSearching) {
      return;
    }

    setOpen(false);
  };

  const performSearchAsync = async () => {
    if (!searchId) {
      return;
    }

    setIsSearching(true);
    setNothingFound(false);

    try {
      const checks = SupportedEntities.map(({ apiCall, redirectUrl }) => {
        return {
          promise: apiCall(searchId),
          url: redirectUrl(searchId),
        };
      });

      await Promise.all(checks.map(({ promise }) => promise));

      for (let i = 0; i < checks.length; i++) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const { promise, url } = checks[i]!;
        const exists = await promise;
        if (exists) {
          setOpen(false);
          navigate(url);
          return;
        }
      }

      setNothingFound(true);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleOnClose} maxWidth="xs" fullWidth>
      <DialogTitle>Find by Id</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          error={nothingFound}
          helperText={nothingFound ? "Nothing was found with this Id" : undefined}
          size="small"
          margin="dense"
          id="search-id"
          label="Id"
          type="text"
          fullWidth
          variant="standard"
          value={searchId}
          onChange={handleInputChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose}>Close</Button>
        <Button
          variant="contained"
          color={"success"}
          onClick={async () => await performSearchAsync()}
          disabled={!searchId}
          loading={isSearching}
        >
          Find
        </Button>
      </DialogActions>
    </Dialog>
  );
}
